import { ChangeEventHandler, Fragment, ReactNode } from 'react'
import { Control, Controller, FieldValues, Path, PathValue } from 'react-hook-form'

import clsx from 'clsx'

import { direction } from 'html2canvas/dist/types/css/property-descriptors/direction'

import { WidgetChoice } from '@/common/form/choice'

import RadioButtonElement from '@/components/view/atoms/radio/RadioButtonElement'
import Text from '@/components/view/atoms/text/Text'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import FormRow from '@/components/view/layouts/form/FormRow'
import TooltipWrapper from '@/components/view/utils/tooltip/TooltipWrapper'

interface RadioButtonBaseProps<T extends FieldValues> {
  name: Path<T>
  choices: WidgetChoice<string | number>[]
  forDisclosure?: boolean
  disabled?: boolean
  subNode?: ReactNode
  input?: boolean
  gap?: 0 | 8 | 12
  direction?: 'vertical' | 'horizon'
}

interface RadioButtonProps<T> extends RadioButtonBaseProps<FieldValues> {
  value: PathValue<T, Path<T>>
  onChange(val: number | string): void
}

export const RadioButton = <T extends FieldValues>({
  name,
  choices,
  forDisclosure,
  disabled = false,
  subNode,
  input = false,
  gap = 0,
  direction = 'vertical',
  value,
  onChange
}: RadioButtonProps<T>) => {
  return (
    <FormRow direction={direction} gap={gap} size={TextBoxSize.FULL}>
      {choices.map((choice) => (
        <Fragment key={choice.value}>
          <RadioButtonElement
            key={choice.value}
            name={name}
            value={choice.value}
            checked={choice.value == value}
            label={
              <>
                {choice.tooltipText ? (
                  <TooltipWrapper text={choice.tooltipText}>
                    <Text dotted>{choice.name}</Text>
                  </TooltipWrapper>
                ) : choice.description ? (
                  <div className={clsx('flex', input ? 'flex-col gap-1' : 'items-center gap-4')}>
                    <span>{choice.name}</span>
                    {choice.description}
                  </div>
                ) : (
                  <>{choice.name}</>
                )}
              </>
            }
            forDisclosure={forDisclosure}
            onChange={() => onChange(choice.value)}
            disabled={disabled || choice.disabled}
            padding
          />
          {choice.value == value && subNode && <>{subNode}</>}
        </Fragment>
      ))}
    </FormRow>
  )
}

export const FormSelectorValue = {
  BOOLEAN: 'boolean'
} as const

export type FormSelectorValue = (typeof FormSelectorValue)[keyof typeof FormSelectorValue]

interface Props<T extends FieldValues> extends RadioButtonBaseProps<T> {
  control: Control<T>
  name: Path<T>
  choices: WidgetChoice<string | number>[]
  forDisclosure?: boolean
  disabled?: boolean
  subNode?: ReactNode
  input?: boolean
  gap?: 0 | 8 | 12
  direction?: 'vertical' | 'horizon'
}

const FormRadioButton = <T extends FieldValues>({
  control,
  name,
  choices,
  forDisclosure,
  disabled = false,
  subNode,
  input = false,
  gap = 0,
  direction = 'vertical'
}: Props<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => (
        <RadioButton
          name={name}
          choices={choices}
          forDisclosure={forDisclosure}
          disabled={disabled}
          subNode={subNode}
          input={input}
          gap={gap}
          direction={direction}
          value={value}
          onChange={onChange}
        />
      )}
    />
  )
}

export default FormRadioButton
